/**
 * Правила оформления части чистыми функциями над её настройками (WEB-3766,
 * юнит «шаблоны»). Одна дверь на два места: редьюсеры редактора части
 * (`editorSlice.ts`: setThemeId, setCloseMode, setLogoUrl, setFaviconUrl,
 * setFaviconApplyToFunnel) и окно «Шаблоны» на карте воронки, где хранилища
 * редактора нет и настройки каждой части правятся этими же функциями.
 * Смена стиля — `withStylePack`, «взять цвета стиля» — `withPackColors`
 * (`packComposition.ts`).
 *
 * WEB-3767, «Оформление» у экрана воронки: «Свой стиль» — шапка квиза, главная
 * кнопка, загрузчик и шрифт воронки (`withQuizHeader`, `withQuizCta`,
 * `withQuizLoader`, `withFunnelFontId`, `withCustomFontFileIn`,
 * `withoutCustomFontIn`) — той же дверью: их зовут редьюсеры
 * (`quizHeader.ts`, `quizCta.ts`, `quizLoader.ts`, `funnelSettingsActions.ts`)
 * и окно оформления на карте и у экрана для части, которой нет в хранилище.
 *
 * WEB-3399, свой стиль организации: правка стиля (`editedCustomStyle` —
 * копия с новым `id`), запись копии (`withCustomStyle` — `stylePackId` и
 * `customStylePack` вместе), свой стиль из тела организации
 * (`copyFromStyleBody`, `withOwnStyleBody`) и тело для сохранения
 * (`ownStyleBodyOf`). Снятие копии при выборе готового — `withStylePack`.
 *
 * WEB-3399, своя палитра (`withOwnPalette`): правка редактора «Своей палитры»
 * и выбор из «Моих палитр» — одна дверь; стиль не трогается (стиль — форма,
 * палитра — цвета).
 */
import {
  DEFAULT_CLOSE_DELAY_SECONDS,
  type PaywallCloseMode,
} from '@/themes/paywallCloseMode';
import type { QuizHeaderSettings } from '@/components/quiz-runtime/quizHeader';
import type { QuizCtaSettings } from '@/components/quiz-runtime/quizCta';
import type { QuizLoaderSettings } from '@/components/quiz-runtime/quizLoader';
import {
  customFontFamilyOf,
  type CustomFont,
  type CustomFontFile,
} from '@/themes/customFonts';
import {
  withCustomFontFile,
  withoutCustomFont,
} from '@/themes/customFontsEdit';
import {
  customStyleOf,
  customStylePackById,
  isCustomStylePackRef,
  type CustomStyleDensity,
  type CustomStylePackCopy,
  type CustomStylePaywall,
  type CustomStyleShape,
  type OrgStyleBody,
} from '@/themes/customStylePack';
import { DEFAULT_STYLE_PACK_ID, STYLE_PACK_IDS } from '@/themes/stylePacks';
import type { StylePackId } from '@/themes/stylePack.types';
import { FONT_IDS } from '@/themes/fonts';
import { withColorsTouched } from './packComposition';
import { mergeQuizLoaderPatch, type QuizLoaderPatch } from './quizLoader';

type Settings = Record<string, unknown>;

/**
 * Тема (undefined — «Без темы»). Зеркало серверного applySetTheme: свои
 * палитра, фон и цвет текста перекрывают тему, поэтому снимаются вместе с
 * выбором; форма, шрифт, логотип и иконка — другая ось, не трогаются.
 */
export function withTheme<T extends object>(
  settings: T,
  themeId: string | undefined,
): T & { themeId?: string; colorsTouched?: boolean } {
  const {
    palette: _p,
    background: _b,
    textColor: _t,
    ...rest
  } = settings as Settings;
  return withColorsTouched({ ...rest, themeId }) as T & {
    themeId?: string;
    colorsTouched?: boolean;
  };
}

/**
 * Режим закрытия пейволла (WEB-853): обязательный — отсутствие полей,
 * задержка живёт только у режима «с задержкой» (по умолчанию 5 секунд).
 */
export function withCloseMode<T extends object>(
  settings: T,
  next: { mode: PaywallCloseMode; delaySeconds?: number },
): T {
  const {
    closeMode: _m,
    closeDelaySeconds: _d,
    ...rest
  } = settings as Settings;
  if (next.mode === 'mandatory') return rest as T;
  return {
    ...rest,
    closeMode: next.mode,
    ...(next.mode === 'delayed' && {
      closeDelaySeconds: next.delaySeconds ?? DEFAULT_CLOSE_DELAY_SECONDS,
    }),
  } as T;
}

/** Поле адреса: обрезать пробелы, пустая строка снимает поле. */
function withUrl<T extends object>(settings: T, key: string, raw: string): T {
  const url = raw.trim();
  const { [key]: _prev, ...rest } = settings as Settings;
  return (url ? { ...(settings as Settings), [key]: url } : rest) as T;
}

/** Логотип воронки (WEB-922). */
export const withLogoUrl = <T extends object>(settings: T, url: string): T =>
  withUrl(settings, 'logoUrl', url);

/** Иконка вкладки (WEB-928). */
export const withFaviconUrl = <T extends object>(settings: T, url: string): T =>
  withUrl(settings, 'faviconUrl', url);

/** «Иконка квиза — на всю воронку»: снятие удаляет поле (WEB-928). */
export function withFaviconApplyToFunnel<T extends object>(
  settings: T,
  on: boolean,
): T {
  const { faviconApplyToFunnel: _prev, ...rest } = settings as Settings;
  return (on ? { ...(settings as Settings), faviconApplyToFunnel: true } : rest) as T;
}

/** Правка поля «своими полями»: `undefined` снимает поле. */
type FieldsPatch<S> = { [K in keyof S]?: S[K] | undefined };

/**
 * Поле-объект настроек «своими полями» (шапка, кнопка, загрузчик): пусто —
 * поля в настройках нет вовсе (отсутствие = вид стиля).
 */
function withFields<T extends object>(
  settings: T,
  key: string,
  next: Record<string, unknown> | undefined,
): T {
  const { [key]: _prev, ...rest } = settings as Settings;
  return (
    next && Object.keys(next).length > 0 ? { ...rest, [key]: next } : rest
  ) as T;
}

/**
 * Шапка квиза (слово владельца 20.10): правка одного поля не трогает
 * соседние; `undefined` и пустая строка снимают поле, адрес крестика
 * обрезается; `null` — вся шапка как в стиле.
 */
export function withQuizHeader<T extends object>(
  settings: T,
  patch: FieldsPatch<QuizHeaderSettings> | null,
): T {
  const prev = (settings as Settings).quizHeader as
    | QuizHeaderSettings
    | undefined;
  const next: Record<string, unknown> = patch ? { ...prev, ...patch } : {};
  if (typeof next.closeUrl === 'string') next.closeUrl = next.closeUrl.trim();
  for (const key of Object.keys(next)) {
    if (next[key] === undefined || next[key] === '') delete next[key];
  }
  return withFields(settings, 'quizHeader', next);
}

/** Главная кнопка квиза (слово владельца 21.10): `undefined` снимает поле. */
export function withQuizCta<T extends object>(
  settings: T,
  patch: FieldsPatch<QuizCtaSettings> | null,
): T {
  const prev = (settings as Settings).quizCta as QuizCtaSettings | undefined;
  const next: Record<string, unknown> = patch ? { ...prev, ...patch } : {};
  for (const key of Object.keys(next)) {
    if (next[key] === undefined) delete next[key];
  }
  return withFields(settings, 'quizCta', next);
}

/** Загрузчик квиза — слиянием `mergeQuizLoaderPatch` (одна функция с полем блока). */
export function withQuizLoader<T extends object>(
  settings: T,
  patch: QuizLoaderPatch | null,
): T {
  const prev = (settings as Settings).quizLoader as
    | QuizLoaderSettings
    | undefined;
  return withFields(
    settings,
    'quizLoader',
    mergeQuizLoaderPatch(prev, patch) as Record<string, unknown> | undefined,
  );
}

/** Шрифт всей воронки: встроенный id или `custom:<семейство>`; пусто снимает. */
export function withFunnelFontId<T extends object>(
  settings: T,
  fontId: string | undefined,
): T {
  const { fontId: _prev, ...rest } = settings as Settings;
  return (fontId ? { ...rest, fontId } : rest) as T;
}

/**
 * Свой шрифт: файл — в семейство (`withCustomFontFile`: пределы, занятые и
 * неверные имена); список не изменился — те же настройки (тот же объект).
 */
export function withCustomFontFileIn<T extends object>(
  settings: T,
  family: string,
  file: CustomFontFile,
): T {
  const list = ((settings as Settings).customFonts as CustomFont[]) ?? [];
  const next = withCustomFontFile(list, family, file);
  return next === list
    ? settings
    : ({ ...(settings as Settings), customFonts: next } as T);
}

/**
 * Убрать своё семейство из настроек: из `customFonts` (пустой список — поля
 * нет) и из шрифта воронки, если он указывал на это семейство. Ссылки блоков
 * чистит только редьюсер открытой части (`removeCustomFontReducer`): это про
 * содержимое, не про настройки.
 */
export function withoutCustomFontIn<T extends object>(
  settings: T,
  family: string,
): T {
  const {
    customFonts: list,
    fontId,
    ...rest
  } = settings as Settings & { customFonts?: CustomFont[]; fontId?: string };
  const next = withoutCustomFont(list ?? [], family);
  const dropFont =
    customFontFamilyOf(fontId)?.toLowerCase() === family.toLowerCase();
  return {
    ...rest,
    ...(next.length > 0 ? { customFonts: next } : {}),
    ...(fontId !== undefined && !dropFont ? { fontId } : {}),
  } as T;
}

/** Правка «Своего стиля»: основа, форма, плотность, пейволл. */
export interface OwnStyleEdit {
  basePackId?: StylePackId;
  shape?: CustomStyleShape;
  density?: CustomStyleDensity;
  paywall?: CustomStylePaywall;
}

const isCatalogPack = (id: unknown): id is StylePackId =>
  typeof id === 'string' && (STYLE_PACK_IDS as readonly string[]).includes(id);

/** Копия воронки: из настроек, иначе — из реестра по ключу. */
function currentCopy(view: Settings): CustomStylePackCopy | undefined {
  const own = customStyleOf(view);
  if (own) return own;
  return isCustomStylePackRef(view.stylePackId)
    ? customStylePackById(view.stylePackId)
    : undefined;
}

/** Пак-основа воронки: копии, иначе готовый пак, иначе пак по умолчанию. */
function basePackOf(view: Settings): StylePackId {
  const copy = currentCopy(view);
  if (copy) return copy.basePackId;
  return isCatalogPack(view.stylePackId) ? view.stylePackId : DEFAULT_STYLE_PACK_ID;
}

const nonEmpty = (o: object) => Object.keys(o).length > 0;

/**
 * Копия после правки «Своего стиля»: от текущей копии воронки (первая правка —
 * от её готового пака, без пака — от `soft-rounded`), с новым `id`;
 * `sourceId` снимается (копия уже не равна сохранённому), имя остаётся.
 */
export function editedCustomStyle(
  view: Settings,
  edit: OwnStyleEdit,
  id: string,
): CustomStylePackCopy {
  const cur = currentCopy(view);
  const shape = { ...cur?.shape, ...edit.shape };
  const paywall = { ...cur?.paywall, ...edit.paywall };
  const density = edit.density ?? cur?.density;
  return {
    id,
    ...(cur?.name !== undefined && { name: cur.name }),
    basePackId: edit.basePackId ?? basePackOf(view),
    ...(nonEmpty(shape) && { shape }),
    ...(density !== undefined && { density }),
    ...(nonEmpty(paywall) && { paywall }),
  };
}

/** Своя палитра: пять цветов редактора (форма тела организации). */
export interface OwnPaletteColors {
  background: string;
  textPrimary: string;
  accent: string;
  surface: string;
  primary: string;
}

/**
 * Своя палитра в настройки: палитра — ровно пять цветов (прочие ключи палитры
 * сняты), свои фон и цвет текста воронки сняты — они накладываются поверх
 * палитры и перекрыли бы её (зеркало серверного `applySetPalette`), признак
 * «трогал цвета». Тема, стиль и копия стиля не трогаются.
 */
export function withOwnPalette<T extends object>(
  settings: T,
  colors: OwnPaletteColors,
): T {
  const { background: _b, textColor: _t, ...rest } = settings as Settings;
  const palette = {
    background: colors.background,
    textPrimary: colors.textPrimary,
    accent: colors.accent,
    surface: colors.surface,
    primary: colors.primary,
  };
  return withColorsTouched({ ...rest, palette }) as T;
}

/** Свой стиль в настройки: ссылка и копия одним шагом. */
export function withCustomStyle<T extends object>(
  settings: T,
  copy: CustomStylePackCopy,
): T {
  return { ...settings, stylePackId: copy.id, customStylePack: copy } as T;
}

/** Тело своего стиля → копия для воронки (зеркало `toFunnelStyleCopy` сервера). */
export function copyFromStyleBody(
  body: OrgStyleBody,
  id: string,
  name: string,
  sourceId: string,
): CustomStylePackCopy {
  return {
    id,
    name,
    sourceId,
    basePackId: body.basePackId,
    ...(body.shape !== undefined && { shape: body.shape }),
    ...(body.density !== undefined && { density: body.density }),
    ...(body.paywall !== undefined && { paywall: body.paywall }),
  };
}

/**
 * Свой стиль из «Моих стилей»: копия и шрифт — в обе части; шапка, кнопка и
 * загрузчик — только квизу; чего в теле нет, снимается («как в стиле»).
 * Цвета не трогаются.
 */
export function withOwnStyleBody<T extends object>(
  settings: T,
  copy: CustomStylePackCopy,
  body: OrgStyleBody,
  part: 'quiz' | 'paywall',
): T {
  const next = withFunnelFontId(withCustomStyle(settings, copy), body.fontId);
  if (part !== 'quiz') return next;
  return withFields(
    withFields(withFields(next, 'quizHeader', body.quizHeader), 'quizCta', body.quizCta),
    'quizLoader',
    body.quizLoader,
  );
}

const objectField = (v: unknown): Record<string, unknown> | undefined =>
  typeof v === 'object' && v !== null && !Array.isArray(v) && nonEmpty(v)
    ? (v as Record<string, unknown>)
    : undefined;

/**
 * Тело своего стиля для сохранения в организацию: правки — из копии воронки
 * (нет копии — только пак-основа), шрифт — только встроенный (свой файлами
 * живёт у воронки), шапка, кнопка и загрузчик — из настроек квиза, если заданы.
 */
export function ownStyleBodyOf(view: Settings): OrgStyleBody {
  const cur = currentCopy(view);
  const fontId = view.fontId;
  const header = objectField(view.quizHeader);
  const cta = objectField(view.quizCta);
  const loader = objectField(view.quizLoader);
  return {
    basePackId: basePackOf(view),
    ...(cur?.shape !== undefined && { shape: cur.shape }),
    ...(cur?.density !== undefined && { density: cur.density }),
    ...(cur?.paywall !== undefined && { paywall: cur.paywall }),
    ...(typeof fontId === 'string' &&
      (FONT_IDS as readonly string[]).includes(fontId) && { fontId }),
    ...(header && { quizHeader: header }),
    ...(cta && { quizCta: cta }),
    ...(loader && { quizLoader: loader }),
  };
}
